import { useEffect, useRef, useState } from "react";
import { Zap, Droplet, Plus, Trash2, Camera, ImagePlus, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UtilityBilling } from "@/components/UtilityBilling";
import { ClearMeterButton } from "@/components/AdminMeterTools";

const METERS = {
  power: { label: "Power", unit: "kWh", icon: Zap, color: "#F59E0B", rateKey: "power_rate" },
  water: { label: "Water", unit: "m³", icon: Droplet, color: "#0EA5E9", rateKey: "water_rate" },
};

const CHART_MARGIN = { top: 5, right: 8, left: -20, bottom: 0 };
const AXIS_TICK = { fontSize: 10, fill: "#94A3B8" };
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8 };
const LINE_DOT = { r: 3 };

const MeterSection = ({ type, unitId, readings, onAdd, onDelete, rate, onViewPhoto, onCleared }) => {
  const meta = METERS[type];
  const Icon = meta.icon;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const [value, setValue] = useState("");
  const [date, setDate] = useState(today);
  const [by, setBy] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);

  const list = readings.filter((r) => r.meter_type === type);
  const chartData = [...list]
    .filter((r) => r.consumption != null)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((r) => ({ date: r.date.slice(5), consumption: r.consumption }));

  const totalCost = list.reduce((a, r) => a + (!r.invoice_id && r.consumption != null && rate ? r.consumption * rate : 0), 0);

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setPhotoFile(f);
    setPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
  };

  const clearPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    if (cameraRef.current) cameraRef.current.value = "";
    if (uploadRef.current) uploadRef.current.value = "";
  };

  const submit = async () => {
    if (value === "" || isNaN(Number(value))) {
      toast.error("Enter a valid meter reading");
      return;
    }
    setSaving(true);
    try {
      let photo_path = null;
      if (photoFile) {
        const res = await api.uploadMeterPhoto(unitId, photoFile);
        photo_path = res.path;
      }
      await onAdd({ meter_type: type, value: Number(value), date, logged_by: by, photo_path });
      setValue("");
      setBy("");
      clearPhoto();
      toast.success(`${meta.label} reading logged`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save reading. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <Icon className="h-4 w-4" style={{ color: meta.color }} />
        <h4 className="font-semibold text-sm text-slate-800">{meta.label} Meter</h4>
        <span className="text-xs font-mono-nums text-slate-400">({meta.unit})</span>
        <ClearMeterButton unitId={unitId} type={type} label={meta.label} onCleared={onCleared} />
        {rate != null && (
          <span className="ml-auto text-xs font-mono-nums text-slate-500" data-testid={`${type}-rate`}>
            ${rate}/{meta.unit}
          </span>
        )}
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-12 gap-2">
          <Input
            data-testid={`${type}-reading-input`}
            type="number"
            step="any"
            inputMode="decimal"
            placeholder={`Reading (${meta.unit})`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="col-span-4 h-9"
          />
          <Input
            data-testid={`${type}-reading-date`}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="col-span-4 h-9"
          />
          <Input
            data-testid={`${type}-reading-by`}
            placeholder="By"
            value={by}
            onChange={(e) => setBy(e.target.value)}
            className="col-span-2 h-9 px-2"
          />
          <Button
            data-testid={`${type}-reading-add`}
            onClick={submit}
            disabled={saving}
            className="col-span-2 h-9 px-2 bg-slate-900 hover:bg-slate-700"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>

        {/* Photo attachment */}
        <div className="flex items-center gap-2">
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            data-testid={`${type}-photo-camera-input`}
            onChange={pickPhoto}
          />
          <input
            ref={uploadRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`${type}-photo-upload-input`}
            onChange={pickPhoto}
          />
          {photoPreview ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onViewPhoto(photoPreview)}
                className="relative h-11 w-11 rounded-md overflow-hidden border border-slate-200"
                data-testid={`${type}-photo-preview`}
              >
                <img src={photoPreview} alt="Meter" className="h-full w-full object-cover" />
              </button>
              <button
                type="button"
                onClick={clearPhoto}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-red-500"
                data-testid={`${type}-photo-remove`}
              >
                <X className="h-3.5 w-3.5" /> Remove photo
              </button>
            </div>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => cameraRef.current?.click()}
                className="h-8 gap-1.5 text-xs border-slate-300"
                data-testid={`${type}-photo-camera-btn`}
              >
                <Camera className="h-3.5 w-3.5" /> Capture Photo
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => uploadRef.current?.click()}
                className="h-8 gap-1.5 text-xs border-slate-300"
                data-testid={`${type}-photo-upload-btn`}
              >
                <ImagePlus className="h-3.5 w-3.5" /> Upload Photo
              </Button>
              <span className="text-[11px] text-slate-400 hidden sm:inline">Attach meter proof (optional)</span>
            </>
          )}
        </div>

        {chartData.length > 1 && (
          <div className="h-28 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Line type="monotone" dataKey="consumption" stroke={meta.color} strokeWidth={2} dot={LINE_DOT} name={`Consumption (${meta.unit})`} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="max-h-48 overflow-auto">
          {list.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-2">No readings logged yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 text-left border-b border-slate-100">
                  <th className="py-1.5 font-medium">Date</th>
                  <th className="py-1.5 font-medium text-right">Reading</th>
                  <th className="py-1.5 font-medium text-right">Used</th>
                  <th className="py-1.5 font-medium text-right">Est. $</th>
                  <th className="py-1.5 font-medium pl-3">By</th>
                  <th className="py-1.5 font-medium text-center">Photo</th>
                  <th className="py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50" data-testid={`reading-row-${r.id}`}>
                    <td className="py-1.5 font-mono-nums">
                      {r.date}
                      {r.invoice_number && <span className="ml-1 text-[9px] font-sans font-semibold rounded bg-emerald-100 text-emerald-700 px-1 py-0.5" data-testid={`reading-invoiced-${r.id}`}>{r.invoice_number}</span>}
                    </td>
                    <td className="py-1.5 text-right font-mono-nums">{r.value}</td>
                    <td className="py-1.5 text-right font-mono-nums font-semibold" style={{ color: meta.color }}>
                      {r.consumption != null ? `+${r.consumption}` : "—"}
                    </td>
                    <td className="py-1.5 text-right font-mono-nums text-slate-600" data-testid={`${type}-cost-${r.id}`}>
                      {r.consumption != null && rate ? `$${(r.consumption * rate).toFixed(2)}` : "—"}
                    </td>
                    <td className="py-1.5 pl-3 text-slate-500 truncate max-w-[70px]">{r.logged_by || "—"}</td>
                    <td className="py-1.5 text-center">
                      {r.photo_path ? (
                        <button
                          onClick={() => onViewPhoto(api.photoUrl(r.photo_path))}
                          className="inline-block h-8 w-8 rounded overflow-hidden border border-slate-200 hover:ring-2 hover:ring-blue-400 transition"
                          data-testid={`reading-photo-thumb-${r.id}`}
                          title="View meter photo"
                        >
                          <img src={api.photoUrl(r.photo_path)} alt="Meter reading" className="h-full w-full object-cover" />
                        </button>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="py-1.5 text-right">
                      {!r.invoice_id && <button data-testid={`reading-delete-${r.id}`} onClick={() => Promise.resolve(onDelete(r.id)).catch((e) => toast.error(e?.response?.data?.detail || "Could not delete reading"))} className="text-slate-300 hover:text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>}
                    </td>
                  </tr>
                ))}
              </tbody>
              {totalCost > 0 && (
                <tfoot>
                  <tr className="border-t border-slate-200">
                    <td colSpan={3} className="py-1.5 text-right font-medium text-slate-500">Not yet invoiced</td>
                    <td className="py-1.5 text-right font-mono-nums font-bold text-slate-900" data-testid={`${type}-total-cost`}>${totalCost.toFixed(2)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export const UtilityLog = ({ unitId, readings, onAdd, onDelete, onInvoiced }) => {
  const [pricing, setPricing] = useState(null);
  const [viewPhoto, setViewPhoto] = useState(null);
  useEffect(() => { api.getPricing().then(setPricing).catch(() => setPricing(null)); }, []);
  return (
    <div className="space-y-3">
      <MeterSection type="power" unitId={unitId} readings={readings} onAdd={onAdd} onDelete={onDelete} rate={pricing?.power_rate} onViewPhoto={setViewPhoto} onCleared={onInvoiced} />
      <MeterSection type="water" unitId={unitId} readings={readings} onAdd={onAdd} onDelete={onDelete} rate={pricing?.water_rate} onViewPhoto={setViewPhoto} onCleared={onInvoiced} />
      <UtilityBilling unitId={unitId} refreshKey={readings} onInvoiced={onInvoiced} />

      <Dialog open={!!viewPhoto} onOpenChange={(o) => !o && setViewPhoto(null)}>
        <DialogContent className="max-w-xl p-2" data-testid="meter-photo-lightbox">
          <DialogTitle className="sr-only">Meter reading photo</DialogTitle>
          {viewPhoto && (
            <img src={viewPhoto} alt="Meter reading" className="w-full h-auto rounded-md" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
